"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  PieChart,
  LineChart,
} from "lucide-react";

// Simple SVG-based chart components
const LineChartComponent = ({ data, color = "#3b82f6", height = 200 }) => {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const range = maxValue - minValue || 1;

  const points = data
    .map((point, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 100 - ((point.value - minValue) / range) * 100;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width="100%" height={height} className="overflow-visible">
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.1" />
        </linearGradient>
      </defs>

      {/* Area fill */}
      <path
        d={`M 0,${height} L ${points
          .split(" ")
          .map((point, i) => {
            const [x] = point.split(",");
            return `${x}%,${(parseFloat(point.split(",")[1]) / 100) * height}`;
          })
          .join(" L ")} L 100%,${height} Z`}
        fill="url(#lineGradient)"
      />

      {/* Line */}
      <polyline
        points={points
          .split(" ")
          .map((point, i) => {
            const [x] = point.split(",");
            return `${x}%,${(parseFloat(point.split(",")[1]) / 100) * height}`;
          })
          .join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data points */}
      {data.map((point, index) => {
        const x = (index / (data.length - 1)) * 100;
        const y = ((point.value - minValue) / range) * height;
        return (
          <circle
            key={index}
            cx={`${x}%`}
            cy={height - y}
            r="4"
            fill={color}
            className="transition-all duration-200 hover:r-6"
          />
        );
      })}
    </svg>
  );
};

const BarChartComponent = ({ data, color = "#3b82f6", height = 200 }) => {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map((d) => d.value));
  const barWidth = 100 / data.length;
  const barSpacing = barWidth * 0.2;

  return (
    <svg width="100%" height={height} className="overflow-visible">
      <defs>
        <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.8" />
          <stop offset="100%" stopColor={color} stopOpacity="0.4" />
        </linearGradient>
      </defs>

      {data.map((item, index) => {
        const barHeight = (item.value / maxValue) * height * 0.8;
        const x = index * barWidth + barSpacing / 2;
        const y = height - barHeight;

        return (
          <g key={index}>
            <rect
              x={`${x}%`}
              y={y}
              width={`${barWidth - barSpacing}%`}
              height={barHeight}
              fill="url(#barGradient)"
              rx="4"
              className="transition-all duration-200 hover:opacity-80"
            />
            <text
              x={`${x + (barWidth - barSpacing) / 2}%`}
              y={y - 8}
              textAnchor="middle"
              className="text-xs font-medium fill-current"
              style={{ fontSize: "10px" }}
            >
              {item.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const PieChartComponent = ({ data, height = 200 }) => {
  if (!data || data.length === 0) return null;

  // Filter out zero values and ensure we have meaningful data
  const validData = data.filter((item) => item.value > 0);
  if (validData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <div className="text-sm">Aucune donnée disponible</div>
          <div className="text-xs">
            Les métriques de conformité seront affichées ici
          </div>
        </div>
      </div>
    );
  }

  const total = validData.reduce((sum, item) => sum + item.value, 0);
  // Healthcare-themed colors: calming blues and green accents
  const colors = [
    "#0ea5e9", // Sky blue - primary healthcare color
    "#10b981", // Emerald green - success/health
    "#06b6d4", // Cyan - secondary healthcare color
    "#3b82f6", // Blue - trust/reliability
    "#8b5cf6", // Violet - innovation
    "#f59e0b", // Amber - warning/attention (used sparingly)
  ];

  let currentAngle = 0;
  const radius = Math.min(height / 2 - 30, 70); // Slightly smaller radius for better centering
  const centerX = 50;
  const centerY = height / 2; // Center vertically in the available height

  return (
    <svg
      width="100%"
      height={height}
      className="overflow-visible"
      viewBox={`0 0 100 ${height}`}
    >
      {validData.map((item, index) => {
        const percentage = (item.value / total) * 100;
        const angle = (percentage / 100) * 360;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;

        const x1 =
          centerX + radius * Math.cos(((startAngle - 90) * Math.PI) / 180);
        const y1 =
          centerY + radius * Math.sin(((startAngle - 90) * Math.PI) / 180);
        const x2 =
          centerX + radius * Math.cos(((endAngle - 90) * Math.PI) / 180);
        const y2 =
          centerY + radius * Math.sin(((endAngle - 90) * Math.PI) / 180);

        const largeArcFlag = angle > 180 ? 1 : 0;

        const pathData = [
          `M ${centerX} ${centerY}`,
          `L ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          "Z",
        ].join(" ");

        currentAngle += angle;

        return (
          <g key={index}>
            <path
              d={pathData}
              fill={colors[index % colors.length]}
              className="transition-all duration-200 hover:opacity-80"
            />
            <text
              x={
                centerX +
                radius *
                  0.7 *
                  Math.cos(((startAngle + angle / 2 - 90) * Math.PI) / 180)
              }
              y={
                centerY +
                radius *
                  0.7 *
                  Math.sin(((startAngle + angle / 2 - 90) * Math.PI) / 180)
              }
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-medium fill-white"
              style={{ fontSize: "10px" }}
            >
              {percentage.toFixed(1)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Main Chart Component
const ChartAreaInteractive = ({
  title,
  description,
  data,
  type = "line",
  color = "#3b82f6",
  height = 200,
  showLegend = true,
  className = "",
  isLoading = false,
}) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  const getChartComponent = () => {
    switch (type) {
      case "line":
        return <LineChartComponent data={data} color={color} height={height} />;
      case "bar":
        return <BarChartComponent data={data} color={color} height={height} />;
      case "pie":
        return <PieChartComponent data={data} height={height} />;
      default:
        return <LineChartComponent data={data} color={color} height={height} />;
    }
  };

  const getChartIcon = () => {
    switch (type) {
      case "line":
        return <LineChart className="h-4 w-4" />;
      case "bar":
        return <BarChart3 className="h-4 w-4" />;
      case "pie":
        return <PieChart className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card
        className={`transition-all duration-300 hover:shadow-lg ${className}`}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`transition-all duration-300 hover:shadow-lg ${className}`}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold flex items-center space-x-2">
              {getChartIcon()}
              <span>{title}</span>
            </CardTitle>
            {description && (
              <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                {description}
              </CardDescription>
            )}
          </div>
          {hoveredItem && (
            <Badge variant="secondary" className="text-xs">
              {hoveredItem.label}: {hoveredItem.value}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div
          className={`relative ${
            type === "pie" ? "flex items-center justify-center" : ""
          }`}
        >
          {getChartComponent()}

          {/* Legend */}
          {showLegend && data && data.length > 0 && type !== "pie" && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              {data.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 text-sm"
                  onMouseEnter={() => setHoveredItem(item)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-gray-600 dark:text-gray-400">
                    {item.label}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Pie chart legend */}
          {showLegend && data && data.length > 0 && type === "pie" && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              {data.map((item, index) => {
                // Healthcare-themed colors: calming blues and green accents
                const colors = [
                  "#0ea5e9", // Sky blue - primary healthcare color
                  "#10b981", // Emerald green - success/health
                  "#06b6d4", // Cyan - secondary healthcare color
                  "#3b82f6", // Blue - trust/reliability
                  "#8b5cf6", // Violet - innovation
                  "#f59e0b", // Amber - warning/attention (used sparingly)
                ];
                return (
                  <div
                    key={index}
                    className="flex items-center space-x-2 text-sm"
                    onMouseEnter={() => setHoveredItem(item)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <span className="text-gray-600 dark:text-gray-400">
                      {item.label}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {item.value}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartAreaInteractive;
